import { Link } from "react-router-dom";
import Breadcrumbs from "@/components/feature/Breadcrumbs";
import SectionHeading from "@/components/feature/SectionHeading";
import ContactForm from "./components/ContactForm";
import { usePageMeta } from "@/hooks/usePageMeta";

const features = [
  { icon: "ri-video-line", title: "海量高清资源", desc: "电影、电视剧、综艺多类别覆盖，持续更新最新内容。" },
  { icon: "ri-flashlight-line", title: "流畅在线播放", desc: "高清画质即点即看，无需下载，随时畅享观影。" },
  { icon: "ri-search-eye-line", title: "便捷查找", desc: "分类浏览搭配关键词搜索，几秒钟找到想看的内容。" },
  { icon: "ri-gift-2-line", title: "免费观看", desc: "注册简化、免费观看，轻松获取优质影视体验。" },
];

export default function AboutPage() {
  usePageMeta({
    title: "关于我们 - 高清视频免费观看",
    description:
      "高清视频免费观看是一个专注高清影视在线播放的视频平台，聚合电影、电视剧、综艺资源，为普通用户提供便捷、丰富、高质量的在线观看体验。",
    keywords: "关于高清视频免费观看,高清视频平台,在线影视网站,免费视频观看",
  });

  return (
    <div className="bg-background-100">
      {/* Banner */}
      <section className="relative w-full h-[280px] md:h-[360px] overflow-hidden bg-primary-950">
        <img
          src="https://readdy.ai/api/search-image?query=Modern%20sleek%20digital%20streaming%20platform%20banner%20with%20floating%20glowing%20video%20screens%20and%20play%20buttons%2C%20deep%20navy%20blue%20background%20with%20soft%20amber%20light%20streaks%2C%20abstract%20technology%20atmosphere%2C%20clean%20minimal%20composition&width=1600&height=600&seq=about-banner-2026&orientation=landscape"
          alt="关于高清视频免费观看"
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-foreground-950/85 via-foreground-950/60 to-foreground-950/30"></div>
        <div className="absolute inset-0 flex items-center">
          <div className="w-full max-w-[1360px] mx-auto px-4 md:px-6 lg:px-8">
            <Breadcrumbs items={[{ label: "首页", to: "/" }, { label: "关于我们" }]} />
            <h1 className="font-heading font-extrabold text-3xl md:text-5xl text-white mt-4">关于我们</h1>
            <p className="mt-3 text-sm md:text-base text-white/80 max-w-2xl">
              高清视频免费观看 —— 让每一次观影都简单、清晰、愉快。
            </p>
          </div>
        </div>
      </section>

      <div className="max-w-[1360px] mx-auto px-4 md:px-6 lg:px-8 py-12 md:py-16">
        {/* Positioning */}
        <section>
          <SectionHeading icon="ri-quill-pen-line" title="平台定位" subtitle="专注高清在线观影的一站式视频平台" />
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-start">
            <div className="lg:col-span-2 space-y-4 text-sm md:text-base text-foreground-600 leading-relaxed">
              <p>
                高清视频免费观看是一个面向广大影视爱好者的在线视频播放平台，致力于用简洁直观的方式，把丰富的高清影视内容呈现给每一位用户。
                我们覆盖电影、电视剧、综艺三大主分类，持续更新热门与经典作品，帮助用户快速发现并观看自己感兴趣的内容。
              </p>
              <p>
                我们相信，好的观影体验应该足够简单：清晰的分类、精准的搜索、贴心的相关推荐，让查找不再费力；高清的画质与流畅的播放，
                让观看真正成为一种放松。无论是通勤路上的短暂时光，还是周末的整段休闲，你都能在这里找到合适的内容。
              </p>
              <p className="text-foreground-500 text-sm">
                说明：当前站点为产品演示版本，页面中的影视名称、简介与图片均为演示数据，播放区域为前端模拟效果，尚未接入真实播放接口。
              </p>
            </div>
            <div className="rounded-lg bg-primary-50 border border-primary-100 p-6">
              <h3 className="font-heading font-bold text-lg text-foreground-950">我们的坚持</h3>
              <ul className="mt-4 space-y-3 text-sm text-foreground-700">
                {["内容丰富 · 持续更新", "分类清晰 · 查找便捷", "高清画质 · 播放流畅", "绿色安全 · 免费观看"].map((item) => (
                  <li key={item} className="flex items-center gap-2">
                    <i className="ri-checkbox-circle-fill text-primary-500"></i>
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        {/* Features */}
        <section className="mt-12 md:mt-16">
          <SectionHeading icon="ri-star-line" title="平台特色" subtitle="为用户打造便捷、丰富、高质量的观看体验" />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
            {features.map((f) => (
              <div key={f.title} className="rounded-lg bg-background-50 border border-background-200 p-6">
                <span className="w-12 h-12 rounded-lg bg-accent-100 text-accent-600 flex items-center justify-center mb-4">
                  <i className={`${f.icon} text-2xl`}></i>
                </span>
                <h3 className="font-heading font-bold text-base text-foreground-950">{f.title}</h3>
                <p className="mt-2 text-sm text-foreground-600 leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Contact */}
        <section className="mt-12 md:mt-16">
          <SectionHeading icon="ri-customer-service-2-line" title="联系方式" subtitle="欢迎通过以下方式与我们取得联系" />
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 md:gap-8">
            <div className="lg:col-span-1 rounded-lg bg-background-50 border border-background-200 p-6">
              <ul className="space-y-5 text-sm">
                {[
                  { icon: "ri-mail-line", label: "邮箱", value: "support@hqvideo-demo.com" },
                  { icon: "ri-phone-line", label: "联系电话", value: "400-000-0000" },
                  { icon: "ri-time-line", label: "服务时间", value: "工作日 9:00 - 18:00" },
                  { icon: "ri-map-pin-line", label: "地址", value: "北京市朝阳区传媒大厦 18 层" },
                ].map((item) => (
                  <li key={item.label} className="flex items-start gap-3">
                    <span className="w-10 h-10 rounded-lg bg-primary-100 text-primary-600 flex items-center justify-center shrink-0">
                      <i className={`${item.icon} text-lg`}></i>
                    </span>
                    <div>
                      <p className="text-foreground-400 text-xs">{item.label}</p>
                      <p className="text-foreground-800 mt-0.5">{item.value}</p>
                    </div>
                  </li>
                ))}
              </ul>
              <div className="mt-6 rounded-md overflow-hidden border border-background-200 h-40">
                <iframe
                  title="公司位置地图"
                  src="https://icanmall.cn/maps?q=Beijing%20Chaoyang&output=embed"
                  className="w-full h-full"
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                ></iframe>
              </div>
            </div>
            <div className="lg:col-span-2">
              <ContactForm />
            </div>
          </div>
        </section>

        {/* Legal */}
        <section className="mt-12 md:mt-16">
          <SectionHeading icon="ri-file-shield-2-line" title="法律声明与版权信息" subtitle="请在使用本站前阅读以下说明" />
          <div className="rounded-lg bg-background-50 border border-background-200 p-6 md:p-8 space-y-4 text-sm text-foreground-600 leading-relaxed">
            <p>
              本站为产品演示性质的高清视频播放平台，页面中所展示的影视名称、简介、导演、演员、评分、图片等信息均为虚构演示数据，
              不代表任何真实作品或机构，也不构成对任何第三方作品的引用或推荐。
            </p>
            <p>
              站内的播放区域为前端模拟演示效果，不包含真实视频流媒体播放能力，亦不提供任何影视资源的下载或存储服务。
              若未来正式上线，所有内容均应以合法授权的方式获取并展示。
            </p>
            <p>
              本站的界面设计、排版与文案（除演示数据外）版权归平台所有，未经许可不得用于商业用途。
              如你发现站内存在侵犯权益的内容，请通过上方联系方式与我们沟通，我们将及时处理。
            </p>
            <p className="text-foreground-400 text-xs pt-2 border-t border-background-200">
              © {new Date().getFullYear()} 高清视频免费观看 · 演示站点 · 保留所有权利
            </p>
          </div>
        </section>

        <div className="mt-10">
          <Link to="/" className="inline-flex items-center gap-1 text-sm font-medium text-primary-600 hover:text-primary-700">
            <i className="ri-arrow-left-line"></i>
            返回首页
          </Link>
        </div>
      </div>
    </div>
  );
}